import { createSlice, nanoid } from '@reduxjs/toolkit';

import { ACCOUNTS, FUNDS, SEGMENTS } from '../data/seed';

/**
 * The prototype's book, for the one screen that still runs on it: Reports (containers/reports/
 * ReportsSection.jsx, domain/statementReports.js and domain/reports.js), which stays a prototype
 * (design 2026-10-02 section 1, "Out").
 *
 * What it keeps is exactly what Reports reads: the seed accounts and their holdings, the seed unit
 * prices, the (empty) transactions and NAV batches its document types list, the documents it has
 * generated, their selectors, generateReport / deleteReport, and MATCH_STATUS for domain/reports.js.
 *
 * Everything else went to the Core through the Console API (IP-89, IP-112): the orders and their
 * approval (Transaction Approval), the NAV runs and prices (Unit Prices), the register (Fund
 * Register) and the bank statement's reconciliation (Bank Statement Transactions). The prototype's
 * NAV batches, repricing, unit ledger, fund summaries and allocation, which settled and valued units
 * in the browser and which nothing ever filled, went with them.
 */

// Defined with the seed data so a seed row can name its own desk; re-exported here because every
// consumer of the segment already imports from this slice.
export { SEGMENTS, SEGMENT_LABELS } from '../data/seed';

/**
 * How a statement line came to be matched — and, for manual matches, whether that match has been
 * approved yet. domain/reports.js labels a line's reconciliation by it.
 */
export const MATCH_STATUS = {
    UNRECONCILED: 'UNRECONCILED',
    AUTO: 'AUTO',
    MANUAL_PENDING: 'MANUAL_PENDING',
    MANUAL_APPROVED: 'MANUAL_APPROVED'
};

/* ── initial state ───────────────────────────────────────────────────────────────────────────── */

const initialPrices = FUNDS.reduce((acc, f) => ({ ...acc, [f.fundId]: f.navPrice }), {});

/**
 * structuredClone keeps the seed module pristine — reducers mutate only the store copy.
 *
 * Seeded accounts default to the unit trust book. A seed row may name its own segment to sit on
 * the wealth management desk instead.
 */
const seedAccounts = () =>
    structuredClone(ACCOUNTS).map((account) => ({
        segment: SEGMENTS.UNIT_TRUST,
        ...account
    }));

const initialState = {
    accounts: seedAccounts(),
    /** Unit price per fund, from the seed fund catalogue. */
    fundPrices: { ...initialPrices },
    transactions: [],
    navBatches: [],
    /**
     * Documents produced by the statement screen, newest first.
     *
     * Each carries the rows it was generated from rather than recomputing at download time: a
     * statement is a snapshot of the book on the day it was issued, and must not silently change
     * when a later transaction settles.
     */
    generatedReports: []
};

/* ── slice ───────────────────────────────────────────────────────────────────────────────────── */

const portfolioSlice = createSlice({
    name: 'portfolio',
    initialState,
    reducers: {
        /**
         * Record a generated document. The caller has already built the rows (the shape depends on
         * the request type, which is domain knowledge, not reducer knowledge) — this stores the
         * snapshot and stamps it.
         */
        generateReport(state, action) {
            const { request, columns, rows, fileName } = action.payload;

            state.generatedReports.unshift({
                id: nanoid(),
                request,
                columns,
                rows,
                fileName,
                rowCount: rows.length,
                generatedAt: new Date().toISOString()
            });
        },

        deleteReport(state, action) {
            state.generatedReports = state.generatedReports.filter(
                (report) => report.id !== action.payload
            );
        }
    }
});

export const {
    generateReport,
    deleteReport
} = portfolioSlice.actions;

/* ── selectors ───────────────────────────────────────────────────────────────────────────────── */

export const selectAccounts = (state) => state.portfolio.accounts;
export const selectFundPrices = (state) => state.portfolio.fundPrices;
export const selectTransactions = (state) => state.portfolio.transactions;
export const selectNavBatches = (state) => state.portfolio.navBatches;
export const selectGeneratedReports = (state) => state.portfolio.generatedReports;

/**
 * Accounts on one desk. Pass no segment to get the whole book — which is what the registry's
 * reports want, since a unit is a unit regardless of which desk sold it.
 */
export const selectAccountsBySegment = (segment) => (state) =>
    segment
        ? state.portfolio.accounts.filter((account) => account.segment === segment)
        : state.portfolio.accounts;

/** Transactions raised against one desk's accounts. */
export const selectTransactionsBySegment = (segment) => (state) => {
    if (!segment) return state.portfolio.transactions;

    const accountIds = new Set(
        state.portfolio.accounts
            .filter((account) => account.segment === segment)
            .map((account) => account.accountId)
    );

    return state.portfolio.transactions.filter((txn) => accountIds.has(txn.accountId));
};

export default portfolioSlice.reducer;
